<!--                  Sign In Modal

When User is checkout out (GiftBasketSideBarButton), there will be a Sign In button
That button brings up this Modal.
User Signs in, it leaves them right where they were before signing in
User Donor Info now populates (after sign in)
Sign in button is hidden after a successful sign in

*I dont think it keeps them signed in after closing the landing page.- Need to verify*

Added a signIn() to user.service.js - this hooks up to backend
  *it already had a sign out function, added a sign in with method: POST

Added a signIn() to user.js (User Store)
  * the function there connects to User Service

Added a create method to sessions_controller
  *this returns json with user data instead of HTML which is needed for this Modal

-->
<script setup lang="ts">
import { ref } from 'vue';
import { useUserStore } from '../../stores/user';
import {useLandingPageStore} from "../../stores/landingPage";
import LoadingSpinner from "./loadingSpinner.vue";
import {useAnalyticEventStore} from "../../stores/analyticEvents";

const props = defineProps({
  // Visibility
  modelValue: {
    type: Boolean,
    required: true
  }
});
//User Store holds the Sing In functionality
const userStore = useUserStore();
const emit = defineEmits(['update:modelValue', 'user-signed-in']);

const errors = ref({});
const signing = ref(false);
const signIn = ref({
  email: "",
  password: ""
});
const rememberMe = ref(false);
const landingStore = useLandingPageStore();
const signedIn = ref(false);
const analyticEventStore = useAnalyticEventStore();

const googleAuthUrl = `/users/auth/google_oauth2?origin=${encodeURIComponent(window.location.href)}`;
const appleAuthUrl = `/users/auth/apple?origin=${encodeURIComponent(window.location.href)}`;
const microsoftAuthUrl = `/users/auth/azure_activedirectory_v2?origin=${encodeURIComponent(window.location.href)}`;

const goToGoogleAuth = () => {
  window.location.href = googleAuthUrl;
};

const goToAppleAuth = () => {
  window.location.href = appleAuthUrl;
};

const goToMicrosoftAuth = () => {
  window.location.href = microsoftAuthUrl;
};

// Email/Password Validation
const validate = () => {
  errors.value = {};
  if (!signIn.value.email) {
    errors.value['email'] = "Email required";
  }
  if (!signIn.value.password) {
    errors.value['password'] = "Password required";
  }
  return Object.entries(errors.value).length === 0;
};

// Sign in Functionality - Calls on userStore sign in method
  const save = async () => {
    if (validate()) {
      signing.value = true;
      errors.value = {};

      try {
        //Credentials from sign in form
        const credentials = {
          user: {
            email: signIn.value.email,
            password: signIn.value.password,
            remember_me: rememberMe.value
          }
        };
        const result = await userStore.signIn(credentials);
        if (result) {
          signedIn.value = true;
          analyticEventStore.logEvent({ action: 'checkout-flow-sign-in' });

          setTimeout(() => {
            // Now emit with the fresh data
            emit('user-signed-in', {
              fName: userStore.user?.first_name || '',
              lName: userStore.user?.last_name || '',
              donorEmail: userStore.user?.email || signIn.value.email
            });
            closeModal();
          }, 500);

        //Error Handling
        } else {
          errors.value['general'] = 'Invalid email or password';
        }
      } catch (error) {
        errors.value['general'] = 'Invalid email or password';
      }
      signing.value = false;
    }

};


const closeModal = () => {
  emit('update:modelValue', false);
};

// Sign Up Navigation
const goToSignUp = () => {
  window.open('/users/sign_up', '_blank');
};

const goToForgotPassword = () => {
  window.open('/users/password/new', '_blank');
};

</script>

<template>
  <div
    v-if="modelValue"
    ref="modalOverlay"
    class="fixed inset-0 flex items-center justify-center z-50"
    tabindex="0"
    @keyup.escape="closeModal"
  >
    <div
      class="fixed inset-0 bg-black opacity-50"
      @click="closeModal"
    />

    <div
      class="relative bg-white p-6 mx-auto rounded-lg shadow-lg transition-all"
      style="width: 500px; max-height: 90vh; overflow-y: auto; height: auto;"
    >
      <!--  Success Message-->
      <div
        v-if="signedIn"
        class="flex items-center justify-center text-default_primary_dark text-lg font-semibold"
        style="min-height: 300px;"
      >
        Log In Successful!
      </div>
      <div
        v-else-if="signing"
        class="flex items-center justify-center"
        style="min-height: 300px;"
      >
        <loading-spinner size="small" />
      </div>


      <!-- Close Button -->
      <button
        class="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
        @click="closeModal"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <form
        v-if="!signedIn && !signing"
        autocomplete="on"
        class="flex flex-col justify-center items-center mt-30"
        @submit.prevent="save"
      >
        <div class="w-80">
          <div class="mb-4">
            <p class="text-3xl font-bold text-default_primary_dark">
              Log In
            </p>
            <p class="text-xs text-gray-500 py-3">
              Please log in to your account.
            </p>
          </div>

          <!--Form Fields -->
          <div class="relative mt-1">
            <i class="fas fa-envelope absolute left-3 top-1/2 transform -translate-y-1/2 text-default_primary text-sm" />
            <input
              v-model="signIn.email"
              type="email"
              name="email"
              autocomplete="email"
              placeholder="Email"
              class="w-full pl-12 pr-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              @keyup.enter="$refs.passwordInput?.focus()"
            >
          </div>
          <span class="text-xs text-red-700">{{ errors['email'] }}</span>

          <div class="relative mt-2">
            <i class="fas fa-lock absolute left-3 top-1/2 transform -translate-y-1/2 text-default_primary text-sm" />
            <input
              ref="passwordInput"
              v-model="signIn.password"
              type="password"
              name="password"
              autocomplete="current-password"
              placeholder="Password"
              class="w-full pl-12 pr-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              @keyup.enter="save"
            >
          </div>
          <span class="text-xs text-red-700">{{ errors['password'] }}</span>
          <span
            v-if="errors['general']"
            class="text-xs text-red-700 block mt-2"
          >{{ errors['general'] }}</span>

          <div class="flex flex-row items-center mt-3">
            <input
              id="checkbox"
              v-model="rememberMe"
              type="checkbox"
            >
            <label
              for="checkbox"
              class="text-xs p-2 mr-auto text-gray-600"
            >Remember Me</label>

            <span
              class="sign-in-link text-xs underline cursor-pointer text-default_primary_dark hover:text-default_primary_darkest"
              @click="goToForgotPassword"
            >
              Forgot Password?
            </span>
          </div>
        </div>

        <!-- Log In Button -->
        <div class="w-full flex flex-col mt-4 items-center">
          <button
            type="submit"
            :class="signedIn ? 'text-white bg-green-600 px-4 py-2 rounded mb-6 block w-80' : 'text-white bg-default_primary_dark hover:bg-default_primary_darkest px-4 py-2 rounded mb-6 block w-80'"
            :disabled="signing || signedIn"
          >
            {{ signing ? 'Logging In...' : 'Log In' }}
          </button>

          <div class="divider w-full">
            <span>OR</span>
          </div>

          <!-- SSO -->
          <div class="sso-buttons">
            <span
              class="sso-btn"
              @click="goToMicrosoftAuth"
            >
              <i class="fab fa-microsoft" />
            </span>
            <span
              class="sso-btn"
              @click="goToGoogleAuth"
            >
              <i class="fab fa-google" />
            </span>
            <span
              class="sso-btn"
              @click="goToAppleAuth"
            >
              <i class="fab fa-apple" />
            </span>
          </div>

          <!-- Sign Up -->
          <span class="text-xs text-gray-600 text-center mt-4 mb-8">
            Don't have an account?
            <span
              class="sign-in-link text-xs underline cursor-pointer text-default_primary_dark hover:text-default_primary_darkest ml-1"
              @click="goToSignUp"
            >
              Sign Up
            </span>
          </span>
        </div>
      </form>

      <!-- Powered by AllGive -->
      <div class="absolute bottom-3 left-3 flex flex-row items-center gap-2">
        <img
          src="@/assets/logos/allgive_icon.png"
          alt="AllGive Logo"
          class="h-4"
        >
        <span class="text-xs text-gray-400">Powered by <a
          href="https://www.allgive.online"
          class="underline"
        >AllGive.online</a></span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
input[type="checkbox"] {
  width: 12px !important;
  height: 12px !important;
  border: 1px solid #085E2DFF !important;
  border-radius: 2px;
  background-color: white;
  cursor: pointer;
  flex-shrink: 0;
  padding-right: 5px;

  &:checked {
    accent-color: #085E2DFF !important;
    border-color: #085E2DFF !important;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7.5 7.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6 10.293l7.146-7.147a .5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;

  }
}

input::placeholder {
  padding-left: 0;
  text-indent: 0;
}

input[type="email"], input[type="password"] {
  padding-left: 48px !important;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin: 5px 0;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.divider::after {
  background: #e5e7eb;
}

.divider span {
  padding: 0 12px;
}

.sso-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
}

.sso-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 42px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: white;
  font-size: 1.1rem;
  color: #339B33;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.sso-btn:hover {
  background-color: #f3f4f6;
}

</style>
